import { supabase } from './supabaseClient';
import type { CartItem } from '../context/CartContext';

export interface Order {
  id: string;
  userId: string;
  items: OrderItem[];
  totalAmount: number;
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  shippingAddress: ShippingAddress;
  paymentMethod: string;
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded';
  trackingNumber?: string;
  estimatedDelivery?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  price: number;
  totalPrice: number;
}

export interface ShippingAddress {
  fullName: string;
  phoneNumber: string;
  county: string;
  town: string;
  address: string;
  postalCode?: string;
  additionalInfo?: string;
}

export interface PaymentDetails {
  method: 'mpesa' | 'card' | 'bank_transfer' | 'cash_on_delivery';
  phoneNumber?: string; // For M-Pesa
  cardDetails?: {
    cardNumber: string;
    expiryDate: string;
    cvv: string;
    cardholderName: string;
  };
  bankDetails?: {
    accountNumber: string;
    bankName: string;
    accountName: string;
  };
}

export class CartService {
  // Save cart to database for authenticated users
  static async saveCartToDatabase(userId: string, cartItems: CartItem[]): Promise<void> {
    try {
      // First, clear existing cart items for this user
      await supabase
        .from('cart_items')
        .delete()
        .eq('user_id', userId);

      // Insert new cart items
      if (cartItems.length > 0) {
        const cartData = cartItems.map(item => ({
          user_id: userId,
          product_id: item.id,
          quantity: item.quantity,
          created_at: new Date().toISOString()
        }));

        const { error } = await supabase
          .from('cart_items')
          .insert(cartData);

        if (error) {
          console.error('Error saving cart to database:', error);
        }
      }
    } catch (error) {
      console.error('Error in saveCartToDatabase:', error);
    }
  }

  // Load cart from database for authenticated users
  static async loadCartFromDatabase(userId: string): Promise<CartItem[]> {
    try {
      const { data, error } = await supabase
        .from('cart_items')
        .select(`
          quantity,
          product:products (
            id,
            name,
            price,
            unit,
            image
          )
        `)
        .eq('user_id', userId);

      if (error) {
        console.error('Error loading cart from database:', error);
        return [];
      }

      if (!data) return [];

      return data.map(item => {
        const product = item.product as any;
        return {
          id: product.id,
          name: product.name,
          price: product.price,
          quantity: item.quantity,
          image: product.image || '',
          unit: product.unit || '',
          seller: {
            name: 'Shop Seller', // Simplified for now to avoid join errors
            location: 'Unknown'
          }
        };
      });
    } catch (error) {
      console.error('Error in loadCartFromDatabase:', error);
      return [];
    }
  }

  // Create order from cart
  static async createOrder(
    userId: string,
    cartItems: CartItem[],
    shippingAddress: ShippingAddress,
    paymentDetails: PaymentDetails
  ): Promise<Order> {
    try {
      const totalAmount = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);

      // Create order record
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .insert({
          user_id: userId,
          total_amount: totalAmount,
          status: 'pending',
          payment_status: 'pending',
          shipping_address: shippingAddress,
          payment_method: paymentDetails.method,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // Create order items
      const orderItems = cartItems.map(item => ({
        order_id: orderData.id,
        product_id: item.id,
        product_name: item.name,
        quantity: item.quantity,
        price: item.price,
        total_price: item.price * item.quantity
      }));

      const { data: itemsData, error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems)
        .select();

      if (itemsError) throw itemsError;

      // Process payment based on method
      let paymentResult;
      switch (paymentDetails.method) {
        case 'mpesa':
          paymentResult = await this.processMpesaPayment(orderData.id, totalAmount, paymentDetails.phoneNumber!);
          break;
        case 'card':
          paymentResult = await this.processCardPayment(orderData.id, totalAmount, paymentDetails.cardDetails!);
          break;
        case 'cash_on_delivery':
          paymentResult = { success: true, transactionId: 'COD_' + orderData.id };
          break;
        default:
          throw new Error('Unsupported payment method');
      }

      // Update order with payment result
      if (paymentResult.success) {
        await supabase
          .from('orders')
          .update({
            payment_status: paymentDetails.method === 'cash_on_delivery' ? 'pending' : 'paid',
            status: 'confirmed',
            transaction_id: paymentResult.transactionId,
            updated_at: new Date().toISOString()
          })
          .eq('id', orderData.id);

        // Clear cart after successful order
        await this.clearCartFromDatabase(userId);
      }

      return {
        id: orderData.id,
        userId: orderData.user_id,
        items: itemsData.map(item => ({
          id: item.id,
          productId: item.product_id,
          productName: item.product_name,
          quantity: item.quantity,
          price: item.price,
          totalPrice: item.total_price
        })),
        totalAmount: orderData.total_amount,
        status: orderData.status,
        shippingAddress: orderData.shipping_address,
        paymentMethod: orderData.payment_method,
        paymentStatus: orderData.payment_status,
        trackingNumber: orderData.tracking_number,
        estimatedDelivery: orderData.estimated_delivery,
        createdAt: orderData.created_at,
        updatedAt: orderData.updated_at
      };
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  // Get user orders
  static async getUserOrders(userId: string): Promise<Order[]> {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items(*)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(order => ({
        id: order.id,
        userId: order.user_id,
        items: order.order_items.map((item: any) => ({
          id: item.id,
          productId: item.product_id,
          productName: item.product_name,
          quantity: item.quantity,
          price: item.price,
          totalPrice: item.total_price
        })),
        totalAmount: order.total_amount,
        status: order.status,
        shippingAddress: order.shipping_address,
        paymentMethod: order.payment_method,
        paymentStatus: order.payment_status,
        trackingNumber: order.tracking_number,
        estimatedDelivery: order.estimated_delivery,
        createdAt: order.created_at,
        updatedAt: order.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching user orders:', error);
      return [];
    }
  }

  // Get order by ID
  static async getOrderById(orderId: string): Promise<Order | null> {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items(*)
        `)
        .eq('id', orderId)
        .single();

      if (error) throw error;

      return {
        id: data.id,
        userId: data.user_id,
        items: data.order_items.map((item: any) => ({
          id: item.id,
          productId: item.product_id,
          productName: item.product_name,
          quantity: item.quantity,
          price: item.price,
          totalPrice: item.total_price
        })),
        totalAmount: data.total_amount,
        status: data.status,
        shippingAddress: data.shipping_address,
        paymentMethod: data.payment_method,
        paymentStatus: data.payment_status,
        trackingNumber: data.tracking_number,
        estimatedDelivery: data.estimated_delivery,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
    } catch (error) {
      console.error('Error fetching order:', error);
      return null;
    }
  }

  // Update order status
  static async updateOrderStatus(orderId: string, status: Order['status'], trackingNumber?: string): Promise<void> {
    try {
      const updateData: any = {
        status,
        updated_at: new Date().toISOString()
      };

      if (trackingNumber) {
        updateData.tracking_number = trackingNumber;
      }

      if (status === 'shipped') {
        // Calculate estimated delivery (3-5 business days)
        const estimatedDelivery = new Date();
        estimatedDelivery.setDate(estimatedDelivery.getDate() + 4);
        updateData.estimated_delivery = estimatedDelivery.toISOString();
      }

      const { error } = await supabase
        .from('orders')
        .update(updateData)
        .eq('id', orderId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  // Clear cart from database
  static async clearCartFromDatabase(userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('user_id', userId);

      if (error) {
        console.error('Error clearing cart from database:', error);
      }
    } catch (error) {
      console.error('Error in clearCartFromDatabase:', error);
    }
  }

  // Payment processing methods
  private static async processMpesaPayment(orderId: string, amount: number, phoneNumber: string) {
    try {
      // Simulate M-Pesa STK push with a delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock successful payment for demo purposes
      return {
        success: true,
        transactionId: `MPESA_${Date.now()}_${Math.floor(Math.random() * 1000)}`
      };
    } catch (error) {
      console.error('M-Pesa payment error:', error);
      return {
        success: false,
        error: 'Payment processing failed'
      };
    }
  }

  private static async processCardPayment(orderId: string, amount: number, cardDetails: any) {
    try {
      // Simulate card payment processing with a delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Mock successful payment for demo purposes
      return {
        success: true,
        transactionId: `CARD_${Date.now()}_${Math.floor(Math.random() * 1000)}`
      };
    } catch (error) {
      console.error('Card payment error:', error);
      return {
        success: false,
        error: 'Payment processing failed'
      };
    }
  }

  // Inventory management
  static async updateProductStock(productId: string, quantityPurchased: number): Promise<void> {
    try {
      // Get current stock
      const { data: product, error: fetchError } = await supabase
        .from('marketplace')
        .select('stock')
        .eq('id', productId)
        .single();

      if (fetchError) throw fetchError;

      // Update stock
      const newStock = Math.max(0, product.stock - quantityPurchased);
      const { error: updateError } = await supabase
        .from('marketplace')
        .update({ stock: newStock })
        .eq('id', productId);

      if (updateError) throw updateError;
    } catch (error) {
      console.error('Error updating product stock:', error);
    }
  }
}

export default CartService;